import {
  Alert,
  Box,
  ButtonBase,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import ScienceOutlined from '@mui/icons-material/ScienceOutlined';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useSearchParams } from 'react-router';
import { api } from '@/api';
import type { DevUser } from '@/api/types';
import { getAuthErrorMessage, useAuth } from '@/auth';
import type { RedirectState } from '@/auth/guards';
import { UserAvatar } from '@/components/UserAvatar';
import { useTenant } from '@/tenant/useTenant';
import { AuthHeader } from './AuthHeader';
import { FormError } from './FormError';
import { GoogleButton } from './GoogleButton';
import { useAuthAction } from './useAuthAction';

const ROLE_LABEL: Record<string, string> = {
  platform_admin: 'Admin da plataforma',
  tenant_admin: 'Admin',
  professional: 'Profissional',
  end_user: 'Cliente',
};

export function LoginPage() {
  const { signInWithGoogle } = useAuth();
  const { config, displayName, isPlatform } = useTenant();
  const location = useLocation();
  const [params] = useSearchParams();
  const { pending, error, run } = useAuthAction();
  const from = (location.state as RedirectState | null)?.from ?? params.get('next') ?? '/';
  const redirectError = params.get('error');
  const branding = config.branding;

  const title = isPlatform
    ? `Console ${config.platformName}`
    : (branding?.welcomeTitle ?? 'Entrar');
  const subtitle = isPlatform
    ? 'Acesso restrito à operação da plataforma.'
    : (branding?.welcomeMessage ?? `Acesse sua conta em ${displayName}.`);

  return (
    <>
      <AuthHeader title={title} subtitle={subtitle} />
      <FormError message={error ?? (redirectError ? getAuthErrorMessage(redirectError) : null)} />
      {config.auth.google ? (
        <GoogleButton
          loading={pending === 'google'}
          disabled={Boolean(pending)}
          onClick={() => void run('google', () => signInWithGoogle({ returnTo: from }))}
        />
      ) : (
        <Alert severity="info">
          O login com Google ainda não foi configurado neste servidor
          {config.auth.devLogin ? ': use o acesso de desenvolvimento abaixo.' : '.'}
        </Alert>
      )}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
        Usamos o Google só para confirmar quem é você. Nenhum acesso aos seus arquivos é pedido
        agora.
      </Typography>
      {config.auth.devLogin && <DevLoginPanel />}
    </>
  );
}

/** Development only: sign in as any seeded user of this space, no Google needed. */
function DevLoginPanel() {
  const { signInAsDevUser } = useAuth();
  const { pending, error, run } = useAuthAction();
  const users = useQuery({
    queryKey: ['dev-users'],
    queryFn: () => api.get<DevUser[]>('/auth/dev/users', { auth: false }),
  });

  return (
    <Box sx={{ mt: 3 }}>
      <Divider sx={{ mb: 2 }}>
        <Chip
          size="small"
          variant="soft"
          color="warning"
          icon={<ScienceOutlined />}
          label="Acesso de desenvolvimento"
        />
      </Divider>
      <FormError message={error} />
      {users.isPending && <CircularProgress size={24} sx={{ display: 'block', mx: 'auto' }} />}
      {users.data?.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          Nenhum usuário neste espaço. Rode <code>make seed</code>.
        </Typography>
      )}
      <Stack spacing={1}>
        {users.data?.map((user) => (
          <ButtonBase
            key={user.email}
            disabled={Boolean(pending)}
            onClick={() => void run(user.email, () => signInAsDevUser(user.email))}
            sx={(theme) => ({
              justifyContent: 'flex-start',
              gap: 1.5,
              p: 1,
              borderRadius: `${Math.min(Number(theme.shape.borderRadius), 14)}px`,
              border: `1px solid ${theme.vars.palette.divider}`,
              textAlign: 'left',
              '&:hover': { bgcolor: theme.vars.palette.action.hover },
            })}
          >
            {pending === user.email ? (
              <CircularProgress size={32} />
            ) : (
              <UserAvatar
                user={{
                  id: user.email,
                  email: user.email,
                  name: user.name ?? null,
                  photoUrl: null,
                  emailVerified: true,
                  signInMethod: 'unknown',
                }}
                sx={{ width: 32, height: 32, fontSize: '0.75rem' }}
              />
            )}
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle2" noWrap>
                {user.name ?? user.email}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap component="div">
                {user.email} · {user.roles.map((r) => ROLE_LABEL[r] ?? r).join(', ')}
              </Typography>
            </Box>
          </ButtonBase>
        ))}
      </Stack>
    </Box>
  );
}
